import {useId} from 'react';
import {ContentHints} from '@/components/app/hint';
import {cn} from '@/lib/utils';
import {Icon} from '../Icon';
import styles from './AdaptiveCheckbox.module.css';
export interface AdaptiveCheckboxProps {
  checked?: boolean;
  color?: string;
  disabled?: boolean;
  info?: string;
  label: string;
  onChange?: (event: {checked: boolean}) => void;
  pending?: boolean;
  radio?: boolean;
  readOnly?: boolean;
}
export const AdaptiveCheckbox = ({
  checked,
  color = 'gray-400',
  disabled,
  info,
  onChange,
  label,
  pending,
  radio,
  readOnly,
}: AdaptiveCheckboxProps) => {
  const labelId = useId();
  const infoId = `${labelId}-info`;
  const ElementType = checked ? 'div' : 'span';
  return (
    <ElementType
      className={cn(styles.adaptiveCheckbox, 'flex items-center gap-2 min-w-0 w-full', {
        [styles.disabled]: disabled,
        [styles.pending]: pending,
        [styles.checked]: checked,
        'cursor-pointer': !readOnly,
      })}
      onClick={
        readOnly
          ? undefined
          : () =>
              onChange?.({
                checked: !checked,
              })
      }
    >
      <div
        className={cn(styles.box, 'flex shrink-0 items-center justify-center text-xs w-[1rem] h-[1rem]', {
          [`text-white   rounded-full   bg-${color}`]: readOnly,
          [`text-${color}   ${radio ? 'rounded-full' : 'rounded-sm'}   border   border-${color}`]: !readOnly,
        })}
        style={{
          transition: 'all ease-in-out .5s',
        }}
      >
        {!readOnly && checked && !radio && (
          <Icon
            className="text-[0.625rem]"
            icon="check"
            style={{
              marginLeft: 1,
            }}
          />
        )}
        {!readOnly && checked && radio && (
          <div
            className={`bg-${color} rounded-full`}
            style={{
              width: '0.5rem',
              height: '0.5rem',
            }}
          />
        )}
      </div>
      <span
        className={cn('overflow-hidden whitespace-nowrap text-ellipsis min-w-0 text-xs', {
          'opacity-50': disabled,
        })}
        id={labelId}
        style={{
          transition: 'all ease-in-out .5s',
        }}
      >
        <span>{label}</span>
      </span>
      {info != null && (
        <>
          <div className={'flex items-center text-primary'} id={infoId}>
            <Icon icon={'info-circle'} />
          </div>
          <ContentHints target={`#${CSS.escape(infoId)}`}>
            <div className={'w-[20rem] max-w-full'}>{info}</div>
          </ContentHints>
        </>
      )}

      <ContentHints
        shouldShow={(e) => {
          if (e.target instanceof HTMLElement) {
            const child = e.target.querySelector('span');
            if (child instanceof HTMLElement) {
              const {width: childWidth} = child.getBoundingClientRect();
              const {width: targetWidth} = e.target.getBoundingClientRect();
              if (childWidth <= targetWidth) {
                return false;
              }
            }
          }
        }}
        side="top"
        target={`#${CSS.escape(labelId)}`}
      >
        {label}
      </ContentHints>
    </ElementType>
  );
};
